import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ScheduleResult, UpdateTaskDto } from "@myapp/contract";
import { taskApi } from "../service";
import { taskKeys } from "../task.keys";
import { rescheduleKeys } from "../../../reschedule/api/reschedule.keys";
import { scheduleKeys } from "../../../schedule/api/schedule.keys";
import {
  applyEntryPatch,
  ownerEntryPatch,
} from "../../../schedule/model/optimisticEntry";

export function useUpdateTask(id: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateTaskDto) =>
      taskApi.update(id, dto).then((r) => r.data),

    /**
     * The calendar keeps up with the form — the same patch `useUpdateAppointment` applies, matched
     * on `taskId`. `ownerEntryPatch` returns null when the DTO carries no timing, which is exactly
     * what `toUpdateDto` sends for a task that owns no entry yet: that first termin goes through
     * `useScheduleTask` and appears when the server answers.
     */
    onMutate: async (dto) => {
      const patch = ownerEntryPatch(dto);

      const previousQueries = queryClient.getQueriesData<ScheduleResult>({
        queryKey: scheduleKeys.ranges(),
      });

      // Synchronously, before the await — see `useUpdateScheduleEntry` for why the order matters.
      if (patch) {
        queryClient.setQueriesData<ScheduleResult>(
          { queryKey: scheduleKeys.ranges() },
          (old) => (old ? applyEntryPatch(old, { taskId: id }, patch) : old),
        );
      }

      await queryClient.cancelQueries({ queryKey: scheduleKeys.ranges() });

      return { previousQueries };
    },

    // A rejected save — a 409 the user has not confirmed — puts the block back where it was. No
    // invalidation follows on this path, so the restored snapshot is what stands.
    onError: (_err, _vars, context) => {
      for (const [queryKey, data] of context?.previousQueries ?? []) {
        queryClient.setQueryData(queryKey, data);
      }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      // An edit can move the task in time, so the worker calendar branch goes stale as well.
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: rescheduleKeys.count() });
      // The disposition board reads GET /schedule, which is keyed separately from taskKeys — without
      // this an edited task keeps its old slot until the 60s refetchInterval fires.
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}
